import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProductCard } from '../components/ProductCard';
import { products as productsApi, wishlist as wishlistApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { SEO } from '../components/SEO';
import { ChevronDown, Check, X, SlidersHorizontal } from 'lucide-react';

const FilterDropdown = ({ label, value, options, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  const isActive = selected?.value && selected.value !== options[0]?.value;

  return (
    <div className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`
          flex items-center justify-between gap-1 h-8 sm:h-9 px-2.5 sm:px-3.5
          bg-white border rounded-full
          text-[11px] sm:text-xs font-medium
          shadow-[0_1px_3px_rgba(0,0,0,0.04)]
          hover:border-[#C9A84C]/50
          focus:outline-none focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C]/20
          transition-all duration-200 whitespace-nowrap
          ${isActive ? 'border-[#C9A84C] bg-[#C9A84C]/5 text-charcoal' : 'border-[#E2E2E2] text-[#666]'}
        `}
        data-testid={`${testId}-trigger`}
      >
        <span>{selected?.label || label}</span>
        <ChevronDown className={`w-3 h-3 text-[#666] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute top-full left-0 mt-1.5 min-w-[160px] z-50 bg-white border border-[#E2E2E2] rounded-lg shadow-lg py-1 overflow-hidden"
            data-testid={`${testId}-dropdown`}
          >
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => { onChange(option.value); setOpen(false); }}
                  className={`flex items-center justify-between w-full px-3 py-2 text-xs text-left transition-all ${
                    isSelected ? 'bg-[#C9A84C] text-white font-semibold' : 'text-charcoal hover:bg-[#F9F8F6]'
                  }`}
                  data-testid={`${testId}-option-${option.value}`}
                >
                  <span>{option.label}</span>
                  {isSelected && <Check className="w-3.5 h-3.5" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlistIds, setWishlistIds] = useState([]);
  const { user } = useAuth();

  const category = searchParams.get('category') || '';
  const size = searchParams.get('size') || '';
  const sort = searchParams.get('sort') || '';
  const price = searchParams.get('price') || '';

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const params = {};
        if (category) params.category = category;
        if (size) params.size = size;
        if (sort) params.sort = sort;
        const data = await productsApi.getAll(params);
        setAllProducts(data);
      } catch (error) {
        console.error('Failed to load products', error);
      }
      setLoading(false);
    };
    loadProducts();
  }, [category, size, sort]);

  // Client-side price filtering
  useEffect(() => {
    let result = [...allProducts];
    if (price === 'under25') result = result.filter(p => p.price < 25);
    else if (price === 'under50') result = result.filter(p => p.price < 50);
    else if (price === 'under100') result = result.filter(p => p.price < 100);
    else if (price === 'over100') result = result.filter(p => p.price >= 100);
    setFilteredProducts(result);
  }, [allProducts, price]);

  useEffect(() => {
    const loadWishlist = async () => {
      if (!user) { setWishlistIds([]); return; }
      try {
        const data = await wishlistApi.get();
        setWishlistIds((data.items || []).map(i => i.id));
      } catch (e) { console.error(e); }
    };
    loadWishlist();
  }, [user]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    setSearchParams(newParams);
  };

  const clearFilters = () => setSearchParams({});
  const hasFilters = category || size || sort || price;

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'vintage', label: 'Vintage' },
    { value: 'streetwear', label: 'Streetwear' },
    { value: 'jackets', label: 'Jackets' },
    { value: 'tshirts', label: 'T-Shirts' },
  ];

  const sizeOptions = [
    { value: 'all', label: 'All Sizes' },
    { value: 'XS', label: 'XS' },
    { value: 'S', label: 'S' },
    { value: 'M', label: 'M' },
    { value: 'L', label: 'L' },
    { value: 'XL', label: 'XL' },
  ];

  const priceOptions = [
    { value: 'all', label: 'All Prices' },
    { value: 'under25', label: 'Under £25' },
    { value: 'under50', label: 'Under £50' },
    { value: 'under100', label: 'Under £100' },
    { value: 'over100', label: '£100+' },
  ];

  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'newest', label: 'Newest' },
    { value: 'price_asc', label: 'Price: Low–High' },
    { value: 'price_desc', label: 'Price: High–Low' },
  ];

  return (
    <div className="min-h-screen" data-testid="shop-page">
      <SEO title="Shop" description="Browse our curated collection of one-of-a-kind vintage and streetwear pieces." path="/shop" />

      {/* Compact Header */}
      <div className="bg-[#0A0A0A] py-6 md:py-10 text-center" data-testid="shop-header">
        <p className="text-[9px] uppercase tracking-[0.3em] text-[#C9A84C] font-semibold mb-1.5">Curated Vintage & Streetwear</p>
        <h1 className="text-2xl md:text-4xl font-heading font-bold tracking-tight text-white" data-testid="shop-title">The Collection</h1>
        <p className="text-white/40 text-xs md:text-sm mt-1.5" data-testid="shop-subtitle">One-off finds. Never restocked.</p>
      </div>

      <div className="py-4 md:py-6">
        <div className="container-custom">
          {/* Filter Bar */}
          <div className="flex items-center gap-1.5 sm:gap-2.5 mb-4 sm:mb-6 overflow-x-auto pb-0.5 -mx-1 px-1 scrollbar-hide" data-testid="shop-filters">
            <div className="items-center gap-1.5 text-[#666] mr-0.5 hidden sm:flex flex-shrink-0">
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-widest">Filters</span>
            </div>

            <FilterDropdown label="Category" value={category || 'all'} options={categoryOptions} onChange={(val) => updateFilter('category', val === 'all' ? '' : val)} testId="filter-category" />
            <FilterDropdown label="Size" value={size || 'all'} options={sizeOptions} onChange={(val) => updateFilter('size', val === 'all' ? '' : val)} testId="filter-size" />
            <FilterDropdown label="Price" value={price || 'all'} options={priceOptions} onChange={(val) => updateFilter('price', val === 'all' ? '' : val)} testId="filter-price" />
            <FilterDropdown label="Sort" value={sort || 'featured'} options={sortOptions} onChange={(val) => updateFilter('sort', val === 'featured' ? '' : val)} testId="filter-sort" />

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 h-8 sm:h-9 px-2.5 text-[11px] font-medium text-red-500 hover:text-red-600 border border-red-200 hover:border-red-300 rounded-full bg-red-50 transition-all flex-shrink-0 whitespace-nowrap"
                data-testid="clear-filters-button"
              >
                <X className="w-3 h-3" />
                Clear
              </button>
            )}

            <div className="ml-auto hidden sm:block">
              <span className="text-xs text-[#666]" data-testid="product-count">
                {filteredProducts.length} {filteredProducts.length === 1 ? 'piece' : 'pieces'}
              </span>
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="text-center py-16" data-testid="loading-indicator">
              <div className="inline-block w-7 h-7 border-2 border-[#D8CFC2] border-t-[#C9A84C] rounded-full animate-spin mb-3" />
              <p className="text-[#666] text-xs">Loading collection...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16" data-testid="no-products-message">
              <p className="text-[#666] text-base mb-1">No pieces found</p>
              <p className="text-[#666]/50 text-xs">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 md:gap-x-5 gap-y-5 md:gap-y-8" data-testid="products-grid">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} wishlistIds={wishlistIds} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
